import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Store, ExternalLink, ImagePlus, X } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { useVendorMe, useVendorRefresh } from "@/hooks/useVendor";
import PhoneField, { isValidPhoneNumber } from "@/components/PhoneField";

const inputCls = "h-11 w-full rounded-lg border border-line px-3 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15";

export default function VendorShop() {
  const { data: vendor } = useVendorMe();
  const refresh = useVendorRefresh();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const bannerRef = useRef();
  const [uploadingBanner, setUploadingBanner] = useState(false);

  useEffect(() => {
    if (vendor) setForm({ shop_name: vendor.shop_name || "", shop_description: vendor.shop_description || "",
      shop_logo: vendor.shop_logo || "", country: vendor.country || "", phone: vendor.phone || "",
      shop_banner_url: vendor.shop_banner_url || "", shop_banner_title: vendor.shop_banner_title || "",
      shop_banner_subtitle: vendor.shop_banner_subtitle || "", shop_banner_link: vendor.shop_banner_link || "",
      shop_banner_cta: vendor.shop_banner_cta || "", shop_banner_active: vendor.shop_banner_active ?? false });
  }, [vendor]);

  if (!form) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/vendor/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, shop_logo: data.url }));
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const uploadBanner = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingBanner(true);
    try { const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/vendor/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, shop_banner_url: data.url }));
    } catch (err) { toast.error(apiError(err)); } finally { setUploadingBanner(false); }
  };

  const save = async () => {
    if (form.phone && !isValidPhoneNumber(form.phone)) { toast.error("Numéro de téléphone invalide. Vérifiez l'indicatif et le nombre de chiffres."); return; }
    setSaving(true);
    try { await api.put("/vendor/profile", form); toast.success("Boutique mise à jour"); refresh(["vendor-me"]); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="vendor-shop">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-extrabold text-ink">Ma boutique</h1>
        {vendor?.shop_slug && (
          <a href={`/boutique-vendeur/${vendor.shop_slug}`} target="_blank" rel="noreferrer" data-testid="view-public-shop" className="flex items-center gap-2 rounded-full border border-line px-4 py-2 text-sm font-bold text-ink hover:border-brand hover:text-brand">
            <ExternalLink className="h-4 w-4" /> Voir ma page publique
          </a>
        )}
      </div>

      <div className="rounded-2xl border border-line bg-white p-6">
        <div className="flex items-start gap-5">
          <div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadLogo} />
            {form.shop_logo ? (
              <div className="relative">
                <img src={fileUrl(form.shop_logo)} alt="logo" className="h-24 w-24 rounded-2xl border border-line object-cover" />
                <button onClick={() => setForm((f) => ({ ...f, shop_logo: "" }))} className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-rose-600 text-white"><X className="h-3 w-3" /></button>
              </div>
            ) : (
              <button onClick={() => fileRef.current?.click()} data-testid="shop-logo-btn" className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-line text-slate-400 hover:border-brand hover:text-brand">
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><ImagePlus className="h-6 w-6" /><span className="text-[10px] font-bold">Logo</span></>}
              </button>
            )}
          </div>
          <div className="flex-1 space-y-4">
            <label className="block"><span className="mb-1.5 block text-[11px] font-bold uppercase text-slate-500">Nom de boutique</span>
              <input data-testid="shop-name-input" value={form.shop_name} onChange={set("shop_name")} className={inputCls} /></label>
            <label className="block"><span className="mb-1.5 block text-[11px] font-bold uppercase text-slate-500">Description</span>
              <textarea data-testid="shop-desc-input" rows={3} value={form.shop_description} onChange={set("shop_description")} className="w-full rounded-lg border border-line p-3 text-sm focus:border-brand focus:outline-none" /></label>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block"><span className="mb-1.5 block text-[11px] font-bold uppercase text-slate-500">Pays</span>
                <input value={form.country} onChange={set("country")} className={inputCls} /></label>
              <PhoneField testId="vshop" label="Téléphone" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
            </div>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <button onClick={save} data-testid="shop-save" disabled={saving} className="rounded-full bg-brand px-8 py-3 text-sm font-bold text-white disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-line bg-white p-6" data-testid="shop-banner-card">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-extrabold text-ink">Bannière promotionnelle</h2>
            <p className="text-xs text-slate-500">Grande affiche affichée en tête de votre boutique publique.</p>
          </div>
          <label className="flex items-center gap-2 text-xs font-bold text-ink">
            <input type="checkbox" checked={!!form.shop_banner_active} onChange={(e) => setForm((f) => ({ ...f, shop_banner_active: e.target.checked }))} className="h-4 w-4 accent-brand" data-testid="banner-active" />
            {form.shop_banner_active ? "🟢 Affichée" : "⚪ Masquée"}
          </label>
        </div>
        <input ref={bannerRef} type="file" accept="image/*" hidden onChange={uploadBanner} />
        {form.shop_banner_url ? (
          <div className="relative overflow-hidden rounded-2xl border border-line">
            <img src={fileUrl(form.shop_banner_url)} alt="bannière" className="h-40 w-full object-cover" />
            <div className="absolute right-2 top-2 flex gap-2">
              <button onClick={() => bannerRef.current?.click()} data-testid="banner-change" className="rounded-lg bg-white/90 px-3 py-1.5 text-xs font-bold text-ink">Changer</button>
              <button onClick={() => setForm((f) => ({ ...f, shop_banner_url: "" }))} className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/90 text-rose-600"><X className="h-4 w-4" /></button>
            </div>
          </div>
        ) : (
          <button onClick={() => bannerRef.current?.click()} data-testid="banner-upload-btn" className="flex h-40 w-full flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-line text-slate-400 hover:border-brand hover:text-brand">
            {uploadingBanner ? <Loader2 className="h-6 w-6 animate-spin" /> : <><ImagePlus className="h-7 w-7" /><span className="text-xs font-bold">Ajouter une bannière (format paysage)</span></>}
          </button>
        )}
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className="block"><span className="mb-1.5 block text-[11px] font-bold uppercase text-slate-500">Titre</span>
            <input data-testid="banner-title" value={form.shop_banner_title} onChange={set("shop_banner_title")} className={inputCls} placeholder="Soldes -30% cette semaine" /></label>
          <label className="block"><span className="mb-1.5 block text-[11px] font-bold uppercase text-slate-500">Texte du bouton</span>
            <input data-testid="banner-cta" value={form.shop_banner_cta} onChange={set("shop_banner_cta")} className={inputCls} placeholder="Découvrir" /></label>
        </div>
        <label className="mt-4 block"><span className="mb-1.5 block text-[11px] font-bold uppercase text-slate-500">Sous-titre</span>
          <input data-testid="banner-subtitle" value={form.shop_banner_subtitle} onChange={set("shop_banner_subtitle")} className={inputCls} placeholder="Profitez de nos meilleures offres cette semaine" /></label>
        <label className="mt-4 block"><span className="mb-1.5 block text-[11px] font-bold uppercase text-slate-500">Lien du bouton (optionnel)</span>
          <input data-testid="banner-link" value={form.shop_banner_link} onChange={set("shop_banner_link")} className={inputCls} placeholder="/boutique-vendeur/ma-boutique" /></label>
        <div className="mt-5 flex justify-end">
          <button onClick={save} data-testid="banner-save" disabled={saving} className="rounded-full bg-brand px-8 py-3 text-sm font-bold text-white disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer la bannière"}
          </button>
        </div>
      </div>
    </div>
  );
}
